import React, { useState } from "react";
import Chart from "react-apexcharts";
import { cn } from "lib/utils";
import { naira } from "lib/boost";
import { useTheme } from "hooks/use-theme";
import {
  AdMetrics,
  AdResults,
  AgeGenderRow,
  Campaign,
  DailyRow,
  LocationRow,
  PlacementRow,
  placementLabel,
} from "action/ads-action";

export const count = (value: number | string | null | undefined) => Number(value || 0).toLocaleString("en-NG");

/** "3 hours ago", "2 days ago"; falls back to the date for anything older than a month. */
export function timeAgo(iso?: string | null) {
  if (!iso) return null;
  const then = new Date(iso);
  const seconds = Math.round((then.getTime() - Date.now()) / 1000);
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [["day", 86400], ["hour", 3600], ["minute", 60]];
  if (Math.abs(seconds) > 30 * 86400) return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

export const shortDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;

/** The 12 headline numbers, in the order the mockup shows them. */
export function statTiles(totals: AdResults, metrics: AdMetrics) {
  const rate = (value: number | null, render: (v: number) => string) => (value == null ? "-" : render(value));
  return [
    { label: "Spend", value: naira(totals.spend) },
    { label: "Reach", value: count(totals.reach), hint: "People who saw your ad" },
    { label: "Views", value: count(totals.impressions), hint: "Times your ad was shown" },
    { label: "Clicks", value: count(totals.clicks) },
    { label: "CTR", value: rate(metrics.ctr, (v) => `${v.toFixed(2)}%`), hint: "Clicks per 100 views" },
    { label: "CPC", value: rate(metrics.cpc, (v) => naira(v, 2)), hint: "Cost per click" },
    { label: "CPM", value: rate(metrics.cpm, (v) => naira(v, 2)), hint: "Cost per 1,000 views" },
    { label: "Added to cart", value: count(totals.add_to_cart) },
    { label: "Purchases", value: count(totals.purchases) },
    { label: "Cost per purchase", value: rate(metrics.cost_per_purchase, (v) => naira(v, 2)) },
    { label: "Revenue from ads", value: naira(totals.revenue) },
    { label: "ROAS", value: rate(metrics.roas, (v) => `${v.toFixed(2)}x`), hint: "Revenue for every ₦1 spent" },
  ];
}

export function StatTiles({ tiles, className }: { tiles: { label: string; value: string; hint?: string }[]; className?: string }) {
  return (
    <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6", className)}>
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-[14px] border border-default-200 bg-card p-4">
          <div className="text-xs font-medium text-default-600" title={tile.hint}>
            {tile.label}
          </div>
          <div className="mt-1 font-display text-xl font-semibold tabular-nums text-default-900">{tile.value}</div>
        </div>
      ))}
    </div>
  );
}

/** Views → store visits → added to cart → checkouts → purchases, with the share kept at each step. */
export function Funnel({ totals }: { totals: AdResults }) {
  const steps = [
    { label: "Views", value: totals.impressions },
    { label: "Store visits", value: totals.store_visits || totals.clicks },
    { label: "Added to cart", value: totals.add_to_cart },
    { label: "Checkouts", value: totals.checkouts },
    { label: "Purchases", value: totals.purchases },
  ];
  const top = Math.max(...steps.map((s) => s.value), 1);
  return (
    <ol className="space-y-3">
      {steps.map((step, index) => {
        const previous = index > 0 ? steps[index - 1].value : 0;
        const kept = index > 0 && previous > 0 ? (step.value / previous) * 100 : null;
        return (
          <li key={step.label} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-default-700">{step.label}</span>
              <span className="tabular-nums text-default-900">
                <span className="font-semibold">{count(step.value)}</span>
                {kept != null && <span className="ms-2 text-xs text-default-500">{kept.toFixed(1)}% of previous</span>}
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-default-100">
              <div
                className="h-full rounded-full bg-chart-1"
                style={{ width: step.value ? `max(4px, ${(step.value / top) * 100}%)` : 0 }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const SERIES = [
  { key: "spend", label: "Spend" },
  { key: "impressions", label: "Views" },
  { key: "clicks", label: "Clicks" },
  { key: "purchases", label: "Purchases" },
] as const;

/** One measure at a time over the days, so there is never a second y-axis. */
export function PerformanceChart({ daily, height = 280 }: { daily: DailyRow[]; height?: number }) {
  const [measure, setMeasure] = useState<(typeof SERIES)[number]["key"]>("spend");
  const { theme } = useTheme();
  const dark = theme === "dark";
  const color = dark ? "#7c9c3c" : "#4e6b1f";
  const ink = dark ? "#a7ad9c" : "#6d7468";
  const label = SERIES.find((s) => s.key === measure)!.label;
  const format = (v: number) => (measure === "spend" ? naira(v) : count(v));

  const options: any = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "'Inter Variable', Inter, sans-serif" },
    colors: [color],
    stroke: { curve: "smooth", width: 2 },
    fill: { type: "gradient", gradient: { shadeIntensity: 0, opacityFrom: 0.25, opacityTo: 0, stops: [0, 100] } },
    dataLabels: { enabled: false },
    markers: { size: 0, hover: { size: 5 } },
    grid: { borderColor: dark ? "#2c3128" : "#ebece5", strokeDashArray: 4, xaxis: { lines: { show: false } } },
    xaxis: {
      categories: daily.map((d) => shortDate(d.date)),
      labels: { style: { colors: ink, fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: ink, fontSize: "11px" }, formatter: (v: number) => format(Math.round(v)) } },
    tooltip: { theme: dark ? "dark" : "light", y: { formatter: format } },
    legend: { show: false },
  };

  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Measure" className="inline-flex rounded-full bg-default-100 p-1">
        {SERIES.map((series) => (
          <button
            key={series.key}
            type="button"
            role="tab"
            aria-selected={measure === series.key}
            onClick={() => setMeasure(series.key)}
            className={cn(
              "h-8 rounded-full px-3 text-xs font-medium transition-colors",
              measure === series.key ? "bg-card text-default-900 shadow-sm" : "text-default-600 hover:text-default-900",
            )}
          >
            {series.label}
          </button>
        ))}
      </div>
      {daily.length === 0 ? (
        <EmptyNote>Daily results show here once your ad is running.</EmptyNote>
      ) : (
        <Chart
          key={`${measure}-${theme}`}
          type="area"
          height={height}
          options={options}
          series={[{ name: label, data: daily.map((d) => Number(d[measure] || 0)) }]}
        />
      )}
    </div>
  );
}

export function PlacementTable({ rows }: { rows: PlacementRow[] }) {
  if (rows.length === 0) return <EmptyNote>Results by placement show here once your ad is running.</EmptyNote>;
  const top = Math.max(...rows.map((r) => r.impressions), 1);
  const sorted = [...rows].sort((a, b) => b.impressions - a.impressions);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead>
          <tr className="text-left text-xs text-default-500">
            <th className="pb-2 font-medium">Placement</th>
            <th className="pb-2 text-right font-medium">Views</th>
            <th className="pb-2 text-right font-medium">Clicks</th>
            <th className="pb-2 text-right font-medium">Purchases</th>
            <th className="pb-2 text-right font-medium">Spend</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-default-100">
          {sorted.map((row) => (
            <tr key={row.placement}>
              <td className="py-2.5 pe-3">
                <div className="text-default-900">{placementLabel(row.placement)}</div>
                <div className="mt-1 h-1.5 w-full max-w-[160px] rounded-full bg-default-100">
                  <div className="h-full rounded-full bg-chart-1" style={{ width: `${(row.impressions / top) * 100}%` }} />
                </div>
              </td>
              <td className="py-2.5 text-right tabular-nums">{count(row.impressions)}</td>
              <td className="py-2.5 text-right tabular-nums">{count(row.clicks)}</td>
              <td className="py-2.5 text-right tabular-nums">{count(row.purchases)}</td>
              <td className="py-2.5 text-right tabular-nums">{naira(row.spend)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Who saw the ads: women and men side by side for each age group. */
export function AgeGenderBars({ rows }: { rows: AgeGenderRow[] }) {
  if (rows.length === 0) return <EmptyNote>Age and gender show here once your ad is running.</EmptyNote>;
  const top = Math.max(...rows.flatMap((r) => [r.female, r.male]), 1);
  const total = rows.reduce((sum, r) => sum + r.female + r.male, 0) || 1;
  const share = (v: number) => `${Math.round((v / total) * 100)}%`;
  return (
    <div className="space-y-4">
      <div className="flex gap-4 text-xs text-default-600" aria-hidden>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-chart-1" />Women</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-chart-2" />Men</span>
      </div>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.age} className="grid grid-cols-[56px_1fr] items-center gap-3">
            <span className="text-xs text-default-600">{row.age}</span>
            <div className="space-y-0.5">
              {([["Women", row.female, "bg-chart-1"], ["Men", row.male, "bg-chart-2"]] as const).map(([who, value, fill]) => (
                <div key={who} className="flex items-center gap-2" title={`${who} ${row.age}: ${count(value)} (${share(value)})`}>
                  <div className="h-2.5 flex-1">
                    <div className={cn("h-full rounded-e-[4px]", fill)} style={{ width: `${(value / top) * 100}%` }} />
                  </div>
                  <span className="w-9 text-right text-[11px] tabular-nums text-default-600">{share(value)}</span>
                </div>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <span className="sr-only">
        {rows.map((r) => `${r.age}: women ${count(r.female)}, men ${count(r.male)}`).join("; ")}
      </span>
    </div>
  );
}

export function LocationList({ rows }: { rows: LocationRow[] }) {
  if (rows.length === 0) return <EmptyNote>Top locations show here once your ad is running.</EmptyNote>;
  const top = Math.max(...rows.map((r) => r.people), 1);
  return (
    <ol className="space-y-3">
      {rows.slice(0, 6).map((row) => (
        <li key={row.location} className="space-y-1">
          <div className="flex justify-between gap-3 text-sm">
            <span className="text-default-800">{row.location}</span>
            <span className="tabular-nums text-default-900">{count(row.people)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-default-100">
            <div className="h-full rounded-full bg-chart-1" style={{ width: `${(row.people / top) * 100}%` }} />
          </div>
        </li>
      ))}
    </ol>
  );
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: "bg-success/10 text-success",
  IN_REVIEW: "bg-info/10 text-info",
  PAUSED: "bg-default-100 text-default-700",
  COMPLETED: "bg-default-100 text-default-600",
  REJECTED: "bg-destructive/10 text-destructive",
  AWAITING_PAYMENT: "bg-warning/10 text-warning",
};

export function CampaignStatus({ campaign }: { campaign: Pick<Campaign, "status" | "status_label" | "payment_status"> }) {
  const failed = campaign.payment_status === "FAILED";
  const label = failed ? "Payment failed" : campaign.status === "IN_REVIEW" ? "Setting up" : campaign.status_label;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium",
        failed ? STATUS_STYLES.REJECTED : STATUS_STYLES[campaign.status] ?? STATUS_STYLES.PAUSED,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
}

export function EmptyNote({ children }: { children: React.ReactNode }) {
  return <p className="rounded-[14px] bg-default-50 px-4 py-8 text-center text-sm text-default-500">{children}</p>;
}

export function Section({ title, action, children, className }: { title: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-20px border border-default-200 bg-card p-5 md:p-6", className)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-base font-semibold text-default-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
